import { useCallback, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { selectExpandedRows, selectPrimarySecurity } from "../selectors/home"
import { getSecurityKey, isFnOSecurity } from "../util/security"
import { selectUnderlyingOptionsTradeData } from "../selectors/options"
import { selectCommonStore } from "../selectors/common"
import { useDispatch } from "react-redux"
import { setOptionChainExpandedRows } from "../reducer/home.reducer"
import { addStrategyOrder, calculateDifferenceInDays, getOrderForStrategy, StrategiesValue } from "../util/strategyBuilder"
import { setPredefinedStrategy } from "../reducer/strategyBuilder.reducer"
import { selectUnderlyingFuturesTradeData } from "../selectors/futures"
import { fetchOptionChainV2Initial } from "../util/options"
import { matchPath } from "react-router-dom"
import ROUTES from "../assets/routes"
import { OptionChainViewKey } from "../assets/constants"

const useOnStrategySelected = () => {
    const { expressionMode, primary, underlying, key } = useSelector(selectPrimarySecurity)
    const mainSecurity = expressionMode ? null : primary && isFnOSecurity(primary) ? underlying : primary
    const optionChainViewKey = useMemo(() => matchPath(ROUTES.simulator, window.location.pathname) ? OptionChainViewKey.HISTORICAL: OptionChainViewKey.LIVE, [window.location.pathname])
    const underlyingToken = mainSecurity ? getSecurityKey(mainSecurity.exchange, mainSecurity.code) : ''
    const futureTradeData = useSelector(selectUnderlyingFuturesTradeData(underlyingToken))
    const optionsExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))
    const { rightSideWidget } = useSelector(selectCommonStore)
    const dispatch = useDispatch()
    const [loading, setLoading] = useState<boolean>(false)

    const updateExpandedRows = useCallback(
        (rows: string[]) => {
            dispatch(setOptionChainExpandedRows({
                expandedRows: rows,
                optionChainViewKey: optionChainViewKey
            }))
        },
        [dispatch, optionChainViewKey]
    )

    const onStrategySelected = useCallback(
        async (strategyLabel: any) => {
            if (!key || !primary || !optionsExpiries) return
            setLoading(true)
            let newOptionExpiries = [...optionsExpiries]
            if(!newOptionExpiries.length){
                const {optionsData} = await fetchOptionChainV2Initial(primary, dispatch)
                newOptionExpiries = optionsData
            }
            const index = Number(expandedRows.length > 0 ? expandedRows[0] : '0')
            if (expandedRows.length === 0) updateExpandedRows(['0'])
            if(!newOptionExpiries[index]){
                setLoading(false)
                return
            }
            if (newOptionExpiries[index].trades.length === 0) {
                const {optionsData} = await fetchOptionChainV2Initial(primary, dispatch, true, [Number(newOptionExpiries[Number(index)].expirySeconds)], newOptionExpiries)
                newOptionExpiries = optionsData
            }
            const atmStrikeData = newOptionExpiries[index]?.trades.find((t) => t.strikeCol.strike === newOptionExpiries[index].atmStrike)
            if (!atmStrikeData) {
                setLoading(false)
                return
            }
            const sdPercent = newOptionExpiries[index].standardDeviation
            const oneSdWidth = atmStrikeData.strikeCol.strike * (sdPercent / 100)

            if (strategyLabel === StrategiesValue.Long_Calendar_With_Calls || strategyLabel === StrategiesValue.Long_Calendar_With_Puts) {
                const monthExpiries = newOptionExpiries.filter((option) => !option.expiration.weeklyExpiry && !option.derived)
                let firstIndex = index
                let secondIndex: number = -1
                monthExpiries.some((expiry) => {
                    if (calculateDifferenceInDays(expiry.expirySeconds ?? '0', newOptionExpiries[firstIndex].expirySeconds ?? '0') >= 14) {
                        secondIndex = Number(expiry.key)
                        return true
                    }
                    return false
                })
                if (!secondIndex || secondIndex === -1) {
                    firstIndex = 0
                    monthExpiries.some((expiry) => {
                        if (calculateDifferenceInDays(expiry.expirySeconds ?? '0', newOptionExpiries[firstIndex].expirySeconds ?? '0') >= 14) {
                            secondIndex = Number(expiry.key)
                            return true
                        }
                        return false
                    })
                }
                if (newOptionExpiries[secondIndex].trades.length === 0) {
                    const {optionsData} = await fetchOptionChainV2Initial(primary, dispatch, true, [Number(newOptionExpiries[secondIndex].expirySeconds)], newOptionExpiries)
                    newOptionExpiries = optionsData
                }
            }

            const orderStrategy = getOrderForStrategy(strategyLabel, atmStrikeData, oneSdWidth, newOptionExpiries, index, updateExpandedRows)
            dispatch(setPredefinedStrategy(strategyLabel))
            addStrategyOrder(orderStrategy, rightSideWidget, dispatch, newOptionExpiries, underlyingToken, orderStrategy.length>1)
            setLoading(false)
        },
        [rightSideWidget, dispatch, expandedRows, optionsExpiries, updateExpandedRows, key, primary, underlyingToken]
    )

    return {
        onStrategySelected, 
        loading
    }
}

export default useOnStrategySelected
